import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Mail, Loader2, ShieldCheck } from 'lucide-react';
import './WorkerCertificates.css';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  return fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: 'include',
  });
}

const STATUS_LABEL = {
  red: 'Verlopen',
  yellow: 'Verloopt binnenkort',
  green: 'Geldig',
  grey: 'Geen einddatum',
};

const MONTHS_NL = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

// Format a 'YYYY-MM-DD' string without going through Date() — avoids timezone shifts.
function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  if (!y || !m || !d) return String(iso);
  return `${Number(d)} ${MONTHS_NL[Number(m) - 1] || m} ${y}`;
}

const EMPTY_FORM = { naam: '', geldigTot: '', behaaldOp: '', instantie: '', certificaatnummer: '' };

export default function WorkerCertificates({ workerId, onChanged }) {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);      // null = closed, else { id?, ...fields }
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);  // reminder in flight
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`/api/workers/${workerId}/certificates`);
      if (!res.ok) throw new Error('load');
      const data = await res.json();
      setCerts(Array.isArray(data) ? data : []);
    } catch {
      setError('Kon certificaten niet laden.');
    } finally {
      setLoading(false);
    }
  }, [workerId]);

  useEffect(() => { if (workerId) load(); }, [workerId, load]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.naam.trim()) { setError('Certificaatnaam is verplicht.'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        naam: form.naam.trim(),
        geldigTot: form.geldigTot || null,
        behaaldOp: form.behaaldOp || null,
        instantie: form.instantie.trim() || null,
        certificaatnummer: form.certificaatnummer.trim() || null,
      };
      const res = form.id
        ? await authFetch(`/api/certificates/${form.id}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
          })
        : await authFetch(`/api/workers/${workerId}/certificates`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
          });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Opslaan mislukt'); }
      setForm(null);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.message || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (cert) => {
    if (!window.confirm(`Certificaat "${cert.naam}" verwijderen?`)) return;
    try {
      const res = await authFetch(`/api/certificates/${cert.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      await load();
      onChanged?.();
    } catch {
      setError('Verwijderen mislukt.');
    }
  };

  const sendReminder = async (cert) => {
    setBusyId(cert.id);
    setNote('');
    try {
      const res = await authFetch(`/api/certificates/${cert.id}/reminder`, { method: 'POST' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Versturen mislukt');
      setNote(`Herinnering verstuurd naar ${d.sentTo || 'de medewerker'}.`);
      await load();
    } catch (err) {
      setError(err.message || 'Versturen mislukt');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="wcert">
      <div className="wcert-head">
        <h4><ShieldCheck size={15} /> Certificaten</h4>
        {!form && (
          <button type="button" className="wcert-add" onClick={() => { setForm({ ...EMPTY_FORM }); setError(''); }}>
            <Plus size={14} /> Toevoegen
          </button>
        )}
      </div>

      {error && <div className="wcert-error">{error}</div>}
      {note && <div className="wcert-note">{note}</div>}

      {form && (
        <form className="wcert-form" onSubmit={save}>
          <div className="wcert-form-grid">
            <label className="wcert-field wcert-span2">
              <span>Certificaatnaam <b>*</b></span>
              <input value={form.naam} autoFocus placeholder="bijv. VCA Basis"
                onChange={(e) => setForm((f) => ({ ...f, naam: e.target.value }))} />
            </label>
            <label className="wcert-field">
              <span>Geldig tot</span>
              <input type="date" value={form.geldigTot || ''}
                onChange={(e) => setForm((f) => ({ ...f, geldigTot: e.target.value }))} />
            </label>
            <label className="wcert-field">
              <span>Behaald op</span>
              <input type="date" value={form.behaaldOp || ''}
                onChange={(e) => setForm((f) => ({ ...f, behaaldOp: e.target.value }))} />
            </label>
            <label className="wcert-field">
              <span>Instantie</span>
              <input value={form.instantie} placeholder="bijv. SSVV"
                onChange={(e) => setForm((f) => ({ ...f, instantie: e.target.value }))} />
            </label>
            <label className="wcert-field">
              <span>Certificaatnummer</span>
              <input value={form.certificaatnummer} placeholder="optioneel"
                onChange={(e) => setForm((f) => ({ ...f, certificaatnummer: e.target.value }))} />
            </label>
          </div>
          <p className="wcert-hint">Zonder &lsquo;geldig tot&rsquo; krijgt het certificaat geen status en blokkeert het niets.</p>
          <div className="wcert-form-actions">
            <button type="button" className="wcert-btn-ghost" onClick={() => { setForm(null); setError(''); }} disabled={saving}>
              Annuleren
            </button>
            <button type="submit" className="wcert-btn-primary" disabled={saving}>
              {saving ? <Loader2 size={14} className="wcert-spin" /> : null} {form.id ? 'Opslaan' : 'Toevoegen'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="wcert-empty"><Loader2 size={14} className="wcert-spin" /> Laden…</p>
      ) : certs.length === 0 ? (
        <p className="wcert-empty">Nog geen certificaten vastgelegd.</p>
      ) : (
        <ul className="wcert-list">
          {certs.map((c) => (
            <li key={c.id} className={`wcert-row status-${c.status}`}>
              <div className="wcert-main">
                <div className="wcert-line1">
                  <strong>{c.naam}</strong>
                  <span className={`wcert-badge b-${c.status}`}>{STATUS_LABEL[c.status] || c.status}</span>
                </div>
                <div className="wcert-line2">
                  <span>Geldig tot {fmtDate(c.geldigTot)}</span>
                  {c.instantie && <span>· {c.instantie}</span>}
                  {c.certificaatnummer && <span>· nr. {c.certificaatnummer}</span>}
                </div>
                {c.status === 'yellow' && c.lastReminderAt && (
                  <div className="wcert-line3">Laatst herinnerd op {fmtDate(c.lastReminderAt)}</div>
                )}
                {c.status === 'red' && (
                  <div className="wcert-line3">De medewerker krijgt hier automatisch bericht over.</div>
                )}
              </div>
              <div className="wcert-actions">
                {c.status === 'yellow' && (
                  <button type="button" className="wcert-remind" disabled={busyId === c.id}
                    title="Herinnering sturen naar de medewerker"
                    onClick={() => sendReminder(c)}>
                    {busyId === c.id ? <Loader2 size={13} className="wcert-spin" /> : <Mail size={13} />}
                  </button>
                )}
                <button type="button" className="wcert-icon" title="Bewerken"
                  onClick={() => { setError(''); setForm({
                    id: c.id, naam: c.naam || '', geldigTot: c.geldigTot || '', behaaldOp: c.behaaldOp || '',
                    instantie: c.instantie || '', certificaatnummer: c.certificaatnummer || '',
                  }); }}>
                  <Pencil size={13} />
                </button>
                <button type="button" className="wcert-icon wcert-icon-del" title="Verwijderen" onClick={() => remove(c)}>
                  <Trash2 size={13} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
